import _extends from "@babel/runtime/helpers/extends";
import React, { forwardRef, useImperativeHandle, useRef, useEffect, useMemo } from 'react';
import { cls, handleUnit, nextTick } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import Loading from '../loading';
import { useSystem, useWindowSize, getStyleWithVendor, useMountedState } from '../_helpers';

/**
 * 增强版的 img 标签，提供多种图片填充模式，支持图片加载中提示、加载失败提示。
 * @type 信息展示
 * @name 图片
 */
export var BaseImage = /*#__PURE__*/forwardRef(function (props, ref) {
  var system = useSystem();

  var _useWindowSize = useWindowSize(),
      windowWidth = _useWindowSize.windowWidth,
      windowHeight = _useWindowSize.windowHeight;

  var _useMountedState = useMountedState('init'),
      imageStatus = _useMountedState[0],
      setImageStatus = _useMountedState[1];

  var _useMountedState2 = useMountedState(''),
      wrapClass = _useMountedState2[0],
      setWrapClass = _useMountedState2[1];

  var _useMountedState3 = useMountedState(false),
      staticRetrying = _useMountedState3[0],
      setStaticRetrying = _useMountedState3[1];

  var imageRef = useRef(null);
  var imageDomRef = useRef(null);
  var wrapRef = useRef(null);
  var retryCountRef = useRef(0);
  var style = props.style,
      className = props.className,
      _props$status = props.status,
      status = _props$status === void 0 ? 'init' : _props$status,
      src = props.src,
      width = props.width,
      height = props.height,
      _props$alt = props.alt,
      alt = _props$alt === void 0 ? '' : _props$alt,
      _props$fit = props.fit,
      fit = _props$fit === void 0 ? 'cover' : _props$fit,
      _props$position = props.position,
      position = _props$position === void 0 ? 'center' : _props$position,
      radius = props.radius,
      bordered = props.bordered,
      loadingArea = props.loadingArea,
      errorArea = props.errorArea,
      showLoading = props.showLoading,
      showError = props.showError,
      _props$animateDuratio = props.animateDuration,
      animateDuration = _props$animateDuratio === void 0 ? 200 : _props$animateDuratio,
      _props$retryTime = props.retryTime,
      retryTime = _props$retryTime === void 0 ? 0 : _props$retryTime,
      forceHttps = props.forceHttps,
      _props$boxWidth = props.boxWidth,
      boxWidth = _props$boxWidth === void 0 ? 0 : _props$boxWidth,
      _props$boxHeight = props.boxHeight,
      boxHeight = _props$boxHeight === void 0 ? 0 : _props$boxHeight,
      topOverlap = props.topOverlap,
      bottomOverlap = props.bottomOverlap,
      _props$showImage = props.showImage,
      showImage = _props$showImage === void 0 ? true : _props$showImage,
      staticLabel = props.staticLabel,
      onChange = props.onChange,
      onClick = props.onClick,
      _onLoad = props.onLoad,
      onError = props.onError,
      onAutoRetry = props.onAutoRetry;
  var isPreview = Boolean(fit.indexOf('preview') >= 0);
  var actualBoxWidth = boxWidth || windowWidth;
  var actualBoxHeight = boxHeight || windowHeight;
  var attrs = useMemo(function () {
    var imageStyle = {};

    if (radius) {
      imageStyle.borderRadius = handleUnit(radius);
    }

    if (!isPreview) {
      imageStyle.objectFit = fit;
      imageStyle.objectPosition = position;
    }

    return {
      alt: alt,
      src: forceHttps ? src.replace('http://', 'https://') : src,
      className: "image-content" + (bordered ? ' bordered' : '') + " " + system,
      style: imageStyle
    };
  }, [alt, bordered, fit, forceHttps, isPreview, position, radius, src, system]);

  function retry() {
    loadImage(true);
  }

  useImperativeHandle(ref, function () {
    return {
      dom: wrapRef.current,
      image: imageDomRef.current,
      retry: retry
    };
  });
  useEffect(function () {
    retryCountRef.current = 0;
    loadImage();
  }, [attrs, width, height, showImage, staticLabel]);

  function changeStatus(newStatus) {
    setImageStatus(newStatus);
    onChange && onChange(newStatus);
  }

  function replaceChild(newChild) {
    var dom = imageRef.current;

    if (!dom) {
      return;
    }

    if (dom.children && dom.children.length) {
      dom.replaceChild(newChild, dom.children[0]);
    } else {
      dom.appendChild(newChild);
    }
  }

  function loadImage(isFromRetry) {
    if (!showImage || staticLabel) {
      return;
    }

    changeStatus('loading');
    var image = new Image();
    image.className = attrs.className;
    Object.keys(attrs.style).forEach(function (key) {
      image.style[key] = attrs.style[key];
    });

    image.onload = function (evt) {
      imageDomRef.current = image;
      changeStatus('loaded');
      var _image$width = image.width,
          imageWidth = _image$width === void 0 ? 0 : _image$width,
          _image$height = image.height,
          imageHeight = _image$height === void 0 ? 0 : _image$height;
      var extraClass = '';

      if (isPreview) {
        var scale = imageWidth / imageHeight;
        var windowScale = actualBoxWidth / actualBoxHeight;

        if (fit === 'preview-y') {
          if (scale < windowScale) {
            image.style.width = actualBoxWidth + "px";
            image.style.height = actualBoxWidth / scale + "px";
            extraClass = 'preview-overflow-y';
          } else {
            extraClass = 'preview-fit-contain-y';
          }
        } else if (fit === 'preview-x') {
          if (scale > windowScale) {
            image.style.width = actualBoxHeight * scale + "px";
            image.style.height = actualBoxHeight + "px";
            extraClass = 'preview-overflow-x';
          } else {
            extraClass = 'preview-fit-contain-x';
          }
        }
      }

      extraClass && image.classList.add(extraClass);
      setWrapClass(extraClass ? extraClass + "-container" : '');
      replaceChild(image);
      _onLoad && _onLoad(evt, image);
    };

    image.onerror = function (evt) {
      if (isFromRetry || retryCountRef.current >= retryTime) {
        changeStatus('error');
        var placeholder = document.createElement('div');
        replaceChild(placeholder);
        onError && onError(evt);
      } else {
        retryCountRef.current += 1;
        onAutoRetry && onAutoRetry(evt);
        loadImage();
      }
    };

    nextTick(function () {
      image.src = attrs.src;
      image.alt = attrs.alt;
    });
  }

  function handleStaticImageError(e) {
    var evt = e.nativeEvent;

    if (retryCountRef.current >= retryTime) {
      onError && onError(evt);
    } else {
      retryCountRef.current += 1;
      onAutoRetry && onAutoRetry(evt); // 强制刷新一下<img>使其重新加载

      setStaticRetrying(true);
      nextTick(function () {
        setStaticRetrying(false);
      });
    }
  }

  function getImageStyle() {
    if (isPreview) {
      var overflow = fit === 'preview-x' ? {
        overflowX: 'auto'
      } : {
        overflowY: 'auto'
      };
      return _extends({
        width: actualBoxWidth,
        height: actualBoxHeight
      }, overflow);
    }

    return {
      width: Number(width) || width,
      height: Number(height) || height
    };
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref) {
    var prefixCls = _ref.prefixCls;
    return /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-image all-border-box", {
        preview: isPreview
      }, system, wrapClass, className),
      style: _extends({}, getImageStyle(), style || {}),
      ref: wrapRef
    }, bottomOverlap !== null ? /*#__PURE__*/React.createElement("div", {
      className: "image-content image-bottom-overlap",
      style: {
        borderRadius: radius
      }
    }, bottomOverlap || /*#__PURE__*/React.createElement("div", {
      className: "image-placeholder"
    })) : null, /*#__PURE__*/React.createElement("div", {
      className: cls('image-container', imageStatus, {
        animate: Boolean(animateDuration),
        'static-label': staticLabel
      }),
      style: getStyleWithVendor({
        transitionDuration: animateDuration + "ms"
      }),
      onClick: onClick,
      ref: imageRef
    }, staticLabel && showImage && !staticRetrying ? /*#__PURE__*/React.createElement("img", _extends({}, attrs, {
      ref: imageDomRef,
      onLoad: function onLoad(e) {
        return _onLoad && _onLoad(e.nativeEvent, imageDomRef.current);
      },
      onError: handleStaticImageError
    })) : null), showLoading && (status === 'loading' || imageStatus === 'loading') ? /*#__PURE__*/React.createElement("div", {
      className: "image-content image-loading-container",
      style: {
        borderRadius: radius
      }
    }, loadingArea || /*#__PURE__*/React.createElement("div", {
      className: "image-loading"
    }, /*#__PURE__*/React.createElement(Loading, {
      type: "circle",
      className: "loading-icon",
      radius: 8.5,
      stroke: 3
    }))) : null, showError && (status === 'error' || imageStatus === 'error') ? /*#__PURE__*/React.createElement("div", {
      className: "image-content image-error-container",
      onClick: function onClick(e) {
        e.stopPropagation();
        retry();
      },
      style: {
        borderRadius: radius
      }
    }, errorArea || /*#__PURE__*/React.createElement("div", {
      className: "image-retry-load"
    }, "\u91CD\u8BD5")) : null, topOverlap ? /*#__PURE__*/React.createElement("div", {
      className: "image-content image-top-overlap",
      style: {
        borderRadius: radius
      }
    }, topOverlap) : null);
  });
});
export default BaseImage;